import { useMemo } from "react";
import { createLocationAPI } from "@/api/apiFactory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useNoticesQuery(locationId: string | undefined) {
  const locationAPI = useMemo(() => createLocationAPI(), []);

  return useQuery({
    queryKey: ["notices", locationId],
    queryFn: async () => {
      if (!locationId) return [];
      return locationAPI.fetchNotices(locationId);
    },
    enabled: !!locationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

export function useCreateNoticeMutation(locationId: string) {
  const queryClient = useQueryClient();
  const locationAPI = useMemo(() => createLocationAPI(), []);

  return useMutation({
    mutationFn: async (message: string) => {
      return locationAPI.createNotice(locationId, message);
    },
    onSuccess: () => {
      // Invalidate and refetch notices for this location
      queryClient.invalidateQueries({ queryKey: ["notices", locationId] });
      // Also invalidate location queries to update the location data
      queryClient.invalidateQueries({ queryKey: ["location", locationId] });
      // Refresh the locations list so the admin Notices badge counts stay accurate.
      queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
  });
}

export function useDeleteNoticeMutation(locationId: string) {
  const queryClient = useQueryClient();
  const locationAPI = useMemo(() => createLocationAPI(), []);

  return useMutation({
    mutationFn: async (noticeId: string) => {
      return locationAPI.deleteNotice(locationId, noticeId);
    },
    onSuccess: () => {
      // Invalidate and refetch notices for this location
      queryClient.invalidateQueries({ queryKey: ["notices", locationId] });
      // Also invalidate location queries to update the location data
      queryClient.invalidateQueries({ queryKey: ["location", locationId] });
      // Refresh the locations list so the admin Notices badge counts stay accurate.
      queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
  });
}

/**
 * Admin broadcast: post one notice to many locations in a single call. Not tied to a
 * single locationId, so it invalidates every per-location notices query plus the
 * locations list (for the badge counts).
 */
export function useBroadcastNoticeMutation() {
  const queryClient = useQueryClient();
  const locationAPI = useMemo(() => createLocationAPI(), []);

  return useMutation({
    mutationFn: async ({ message, locationIds }: { message: string; locationIds: string[] }) => {
      return locationAPI.broadcastNotice(message, locationIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
      queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
  });
}
